import * as yup from 'yup';

const nikshayValidationSchema = yup.object({
  // Test Details - Basic Required Fields
  ReasonForTesting: yup
    .string()
    .required('Reason for Testing is required'),

  TestType: yup
    .string()
    .required('Test Type is required'),

  // Conditional validation for TypeOfCase - Required for TB and DRTB diagnosis
  TypeOfCase: yup
    .string()
    .when('ReasonForTesting', {
      is: (value) => value === 'Diagnosis of TB' || value === 'Diagnosis of DRTB',
      then: (schema) => schema.required('Type of Case is required'),
      otherwise: (schema) => schema.nullable()
    }),

  // Follow Up Reason - Required for follow-up scenarios
  FollowUpReasonDSTB: yup
    .string()
    .when('ReasonForTesting', {
      is: (value) => value === 'Follow-up of TB' || value === 'Follow-up of DRTB',
      then: (schema) => schema.required('Follow Up Reason is required'),
      otherwise: (schema) => schema.nullable()
    }),

  FollowUpReasonDRTBMonth: yup
  .number()
  .when(['ReasonForTesting', 'FollowUpReasonDSTB'], {
    is: (reasonForTesting, followUpReason) => 
      (reasonForTesting === 'Follow-up of DRTB') && 
      (followUpReason === 'Month'),
    then: (schema) => schema
      .required('Month is required when Follow Up Reason is Month')
      .positive('Month must be a positive number')
      .integer('Month must be a whole number')
      .min(1, 'Month must be at least 1')
      .max(36, 'Month cannot exceed 36'),
    otherwise: (schema) => schema.nullable()
  }),

  // Facility Details - Required Fields
  TestingFacilityState: yup
    .mixed()
    .test('required-state', 'Testing Facility State is required', function(value) {
      return value && value.Value && value.Value.trim() !== '';
    }),

  TestingFacilityDistrict: yup
    .mixed()
    .test('required-district', 'Testing Facility District is required', function(value) {
      return value && value.Value && value.Value.trim() !== '';
    }),

  TestingFacility: yup
    .mixed()
    .test('required-facility', 'Testing Lab is required', function(value) {
      return value && value.Value && value.Value.trim() !== '';
    }),

  // Sample Availability - Required (conditional based on test type)
  SampleAvailability: yup
    .string()
    .when('TestType', {
      is: (testType) => !['Chest X Ray', 'Cytopathology', 'Histopathology', 'Gene Sequencing', 'TST', 'IGRA', 'Cy-Tb', 'Other'].includes(testType),
      then: (schema) => schema.required('Sample Availability is required'),
      otherwise: (schema) => schema.nullable()
    }),

  // Result Availability - Always Required
  ResultAvailability: yup
    .string()
    .required('Result Available is required'),

  // Result Sample ID - Required when both Sample and Result are Present
  ResultSampleId: yup
    .string()
    .when(['SampleAvailability', 'ResultAvailability'], {
      is: (sampleAvail, resultAvail) => sampleAvail === 'Present' && resultAvail === 'Present',
      then: (schema) => schema.required('Sample ID is required'),
      otherwise: (schema) => schema.nullable()
    }),

  // Optional fields (no validation required as per requirement)
  PredominantSymptom: yup.string().nullable(),
  PredominantSymptomDuration: yup.number().min(0, 'Duration cannot be negative').nullable(),
  HCPVisits: yup.number().min(0, 'HCP Visits cannot be negative').nullable(),
  MonthsOfTreatment: yup.number().min(0, 'Months of Treatment cannot be negative').nullable(),
  MonthsSinceEpisode: yup.number().min(0, 'Months since Episode cannot be negative').nullable(),
  CulturePositiveMonth: yup.number().min(0, 'Month cannot be negative').nullable(),
  DiagnosisOfTB: yup.array().nullable(),
  TypeOfPresumptiveDRTB: yup.array().nullable(),

  // Test Results - Conditional validation based on Result Availability
  ResultDateTested: yup
    .string()
    .test('no-future-date', 'Date Tested cannot be in the future', function(value) {
      if (!value) return true;
      const parts = value.split('-');
      if (parts.length !== 3) return true;
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const year = parseInt(parts[2], 10);
      const dateVal = new Date(year, month, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return dateVal <= today;
    })
    .nullable(),

  ResultDateReported: yup
    .string()
    .test('no-future-date', 'Date Reported cannot be in the future', function(value) {
      if (!value) return true;
      const parts = value.split('-');
      if (parts.length !== 3) return true;
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const year = parseInt(parts[2], 10);
      const dateVal = new Date(year, month, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return dateVal <= today;
    })
    .when('ResultAvailability', {
      is: 'Present',
      then: (schema) => schema.required('Date Reported is required'),
      otherwise: (schema) => schema.nullable()
    }),

  FinalInterpretation: yup
    .string()
    .when('ResultAvailability', {
      is: 'Present',
      then: (schema) => schema.required('Final Interpretation is required'),
      otherwise: (schema) => schema.nullable()
    }),

  // FLLPA specific validations
  FLLPA_AddMutationDetails: yup
    .string()
    .when('TestType', {
      is: 'FLLPA',
      then: (schema) => schema.required('Add Mutation Details selection is required'),
      otherwise: (schema) => schema.nullable()
    }),

  FLLPA_Result: yup
    .string()
    .when(['TestType', 'ResultAvailability'], {
      is: (testType, resultAvail) => testType === 'FLLPA' && resultAvail === 'Present',
      then: (schema) => schema.required('FLLPA Result is required'),
      otherwise: (schema) => schema.nullable()
    }),

  // FLLPA Resistance fields - Required when FLLPA_Result is 'M.Tb. detected'
  ResistanceToRifampicin: yup
    .string()
    .when(['TestType', 'FLLPA_Result'], {
      is: (testType, result) => testType === 'FLLPA' && result === 'M.Tb. detected',
      then: (schema) => schema.required('Resistance to Rifampicin is required'),
      otherwise: (schema) => schema.nullable()
    }),

  LowLevelResistanceToIsoniazid: yup
    .string()
    .when(['TestType', 'FLLPA_Result'], {
      is: (testType, result) => testType === 'FLLPA' && result === 'M.Tb. detected',
      then: (schema) => schema.required('Low Level Resistance to Isoniazid is required'),
      otherwise: (schema) => schema.nullable()
    }),

  HighLevelResistanceToIsoniazid: yup
    .string()
    .when(['TestType', 'FLLPA_Result'], {
      is: (testType, result) => testType === 'FLLPA' && result === 'M.Tb. detected',
      then: (schema) => schema.required('High Level Resistance to Isoniazid is required'),
      otherwise: (schema) => schema.nullable()
    }),

  // SLLPA specific validations
  SLLPA_AddMutationDetails: yup
    .string()
    .when('TestType', {
      is: 'SLLPA',
      then: (schema) => schema.required('Add Mutation Details selection is required'),
      otherwise: (schema) => schema.nullable()
    }),

  SLLPA_Result: yup
    .string()
    .when(['TestType', 'ResultAvailability'], {
      is: (testType, resultAvail) => testType === 'SLLPA' && resultAvail === 'Present',
      then: (schema) => schema.required('SLLPA Result is required'),
      otherwise: (schema) => schema.nullable()
    }),

  // SLLPA Resistance fields - Required when SLLPA_Result is 'M.Tb. detected'
  ResistanceToFluoroquinolones: yup
    .string()
    .when(['TestType', 'SLLPA_Result'], {
      is: (testType, result) => testType === 'SLLPA' && result === 'M.Tb. detected',
      then: (schema) => schema.required('Resistance to Fluoroquinolones is required'),
      otherwise: (schema) => schema.nullable()
    }),

  ResistanceToSecondLineInjectableDrugs: yup
    .string()
    .when(['TestType', 'SLLPA_Result'], {
      is: (testType, result) => testType === 'SLLPA' && result === 'M.Tb. detected',
      then: (schema) => schema.required('Resistance to Second Line Injectable Drugs is required'),
      otherwise: (schema) => schema.nullable()
    }),

  ResistanceToSecondLineInjectableDrugsLowLevel: yup
    .string()
    .when(['TestType', 'SLLPA_Result'], {
      is: (testType, result) => testType === 'SLLPA' && result === 'M.Tb. detected',
      then: (schema) => schema.required('Resistance to Second Line Injectable Drugs (Low Level) is required'),
      otherwise: (schema) => schema.nullable()
    }),

  // DST specific validations
  CultureType: yup
    .string()
    .when(['TestType', 'ResultAvailability'], {
      is: (testType, resultAvail) => testType === 'DST' && resultAvail === 'Present',
      then: (schema) => schema.required('Culture Type is required'),
      otherwise: (schema) => schema.nullable()
    }),

  DSTToDrug: yup
    .string()
    .when(['TestType', 'ResultAvailability'], {
      is: (testType, resultAvail) => testType === 'DST' && resultAvail === 'Present',
      then: (schema) => schema.required('DST To Drug is required'),
      otherwise: (schema) => schema.nullable()
    }),

  ResistanceStatus: yup
    .string()
    .when(['TestType', 'ResultAvailability'], {
      is: (testType, resultAvail) => testType === 'DST' && resultAvail === 'Present',
      then: (schema) => schema.required('Resistance Status is required'),
      otherwise: (schema) => schema.nullable()
    }),

  // Text-based test results
  TextResult: yup
    .string()
    .when(['TestType', 'ResultAvailability'], {
      is: (testType, resultAvail) => 
        ['Cytopathology', 'Histopathology', 'Gene Sequencing', 'Pathodetect - MTB, Rif & Inh', 'Other'].includes(testType) && 
        resultAvail === 'Present',
      then: (schema) => schema.required('Result is required'),
      otherwise: (schema) => schema.nullable()
    }),

  // Sample Details validation when Sample Availability is Present
  TestSampleDetails: yup
    .array()
    .when('SampleAvailability', {
      is: 'Present',
      then: (schema) => schema
        .of(
          yup.object({
            sampleMappingOption: yup.string().required('Sample Mapping Option is required'),
            sampleType: yup
              .string()
              .when('sampleMappingOption', {
                is: 'Add New Sample',
                then: (schema) => schema.required('Sample Type is required'),
                otherwise: (schema) => schema.nullable()
              }),
            sputumCollectionDetail: yup
              .string()
              .when(['sampleMappingOption', 'sampleType'], {
                is: (mappingOption, sampleType) => 
                  mappingOption === 'Add New Sample' && sampleType === 'Sputum',
                then: (schema) => schema.required('Sputum Collection Detail is required'),
                otherwise: (schema) => schema.nullable()
              }),
            sampleDescription: yup
              .string()
              .when(['sampleMappingOption', 'sampleType'], {
                is: (mappingOption, sampleType) => 
                  mappingOption === 'Add New Sample' && sampleType === 'Sputum',
                then: (schema) => schema.required('Sample Description is required'),
                otherwise: (schema) => schema.nullable()
              }),
            sampleTypeOther: yup
              .string()
              .when(['sampleMappingOption', 'sampleType'], {
                is: (mappingOption, sampleType) => 
                  mappingOption === 'Add New Sample' && sampleType === 'Other',
                then: (schema) => schema.required('Other sample type specification is required'),
                otherwise: (schema) => schema.nullable()
              }),
            collectionDate: yup
              .string()
              .when('sampleMappingOption', {
                is: 'Add New Sample',
                then: (schema) => schema
                  .required('Collection Date is required')
                  .test('no-future-date', 'Collection Date cannot be in the future', function(value) {
                    if (!value) return true;
                    const parts = value.split('-');
                    if (parts.length !== 3) return true;
                    const day = parseInt(parts[0], 10);
                    const month = parseInt(parts[1], 10) - 1;
                    const year = parseInt(parts[2], 10);
                    const dateVal = new Date(year, month, day);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return dateVal <= today;
                  }),
                otherwise: (schema) => schema.nullable()
              }),
            collectionTime: yup
              .string()
              .when('sampleMappingOption', {
                is: 'Add New Sample',
                then: (schema) => schema
                  .required('Collection Time is required')
                  .test('no-future-time', 'Collection Time cannot be in the future', function(value) {
                    if (!value) return true;
                    const collectionDate = this.parent.collectionDate;
                    if (!collectionDate) return true;
                    
                    const dateParts = collectionDate.split('-');
                    if (dateParts.length !== 3) return true;
                    const day = parseInt(dateParts[0], 10);
                    const month = parseInt(dateParts[1], 10) - 1;
                    const year = parseInt(dateParts[2], 10);
                    const dateVal = new Date(year, month, day);
                    
                    const today = new Date();
                    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                    
                    if (dateVal < todayOnly) return true;
                    if (dateVal > todayOnly) return true;
                    
                    const timeParts = value.split(':');
                    if (timeParts.length !== 2) return true;
                    const hours = parseInt(timeParts[0], 10);
                    const minutes = parseInt(timeParts[1], 10);
                    
                    const selectedTimeMinutes = hours * 60 + minutes;
                    const currentTimeMinutes = today.getHours() * 60 + today.getMinutes();
                    
                    return selectedTimeMinutes <= currentTimeMinutes;
                  }),
                otherwise: (schema) => schema.nullable()
              }),
            collectionSiteState: yup
              .string()
              .when('sampleMappingOption', {
                is: 'Add New Sample',
                then: (schema) => schema.required('Collection Site State is required'),
                otherwise: (schema) => schema.nullable()
              }),
            collectionSiteDistrict: yup
              .string()
              .when('sampleMappingOption', {
                is: 'Add New Sample',
                then: (schema) => schema.required('Collection Site District is required'),
                otherwise: (schema) => schema.nullable()
              }),
            collectionSite: yup
              .string()
              .when('sampleMappingOption', {
                is: 'Add New Sample',
                then: (schema) => schema.required('Collection Site is required'),
                otherwise: (schema) => schema.nullable()
              }),
            mappedSampleId: yup
              .string()
              .when('sampleMappingOption', {
                is: 'Map Existing Sample',
                then: (schema) => schema.required('Mapped Sample ID is required'),
                otherwise: (schema) => schema.nullable()
              })
          })
        )
        .min(1, 'At least one sample is required'),
      otherwise: (schema) => schema.nullable()
    })
});

export default nikshayValidationSchema;